import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { FsLprFleetRead, IFsLprResult, LprIntegrationType, SnLprV3FlockRead } from '../../api';
import { PlateImageDownloadService } from '../../services';
import { LightboxImage } from './models/lightbox-image.model';

@Component({
  selector: 'fs-image-lightbox-viewer',
  templateUrl: './image-lightbox-viewer.component.html',
  styleUrls: ['./image-lightbox-viewer.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        animate('250ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' })),
      ]),
    ]),
  ],
})
export class ImageLightboxViewerComponent implements OnChanges {
  /** The id of the search result */
  @Input() plateId?: string;

  @Input() images: LightboxImage[] = [];

  /** The plate object */
  @Input() plate: FsLprFleetRead;

  /** The cropped image URL */
  @Input() croppedImageUrl: string | null = null;

  /** Whether the lightbox is currently visible */
  @Input() isVisible = false;

  /** Whether it's rendered in details view */
  @Input() isDetailsView: boolean = false;

  /** Used to track if the right sidenav is open or closed */
  @Input() isRightSidenavOpen: boolean = false;

  @Input() plateText: string = '';

  /** Whether lightbox is closed or not */
  @Output() public isClosed = new EventEmitter<void>();
  @Output() lightboxClosed: EventEmitter<void> = new EventEmitter();

  /** Tracks which image index is active */
  public currentIndex: number = 0;

  public hasImageLoadError: boolean = false;

  @HostListener('document:keydown.escape', ['$event'])
  onEscapePressed(event: KeyboardEvent): void {
    this.closeLightbox();
  }

  constructor(private readonly _plateImageDownloadService: PlateImageDownloadService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plateId'] && !changes['plateId'].firstChange) {
      this.currentIndex = 0;
    }
  }

  /**
   * Closes the lightbox and emits an event if it's in details view.
   */
  public closeLightbox(): void {
    this.isVisible = false;
    if (this.isRightSidenavOpen) {
      this.isDetailsView = true;
    }
    this.lightboxClosed.emit();
  }

  /**
   * This method is used to close the right sidenav when the
   * close button in the right sidenav lightbox is clicked.
   */
  public closeRightSidenav(): void {
    this.isRightSidenavOpen = false;
    // Only emit this event if details view is open and is being closed
    if (this.isDetailsView) {
      this.isClosed.emit();
    }
  }

  /**
   * This method is used to open the bigger lightbox when the
   * view larger button in the right sidenav lightbox is clicked.
   */
  public openLightboxFromDetailsView(): void {
    this.isVisible = true;
    this.isDetailsView = false;
  }

  public setPlaceholderImage(event: any): void {
    event.target.src = '/assets/map-icons/No_Image_Found.png';
    this.hasImageLoadError = true;
  }

  get hasExternalFlockV3Link(): boolean {
    return (
      this.plate?.integratedLprType === LprIntegrationType.FLOCK_V3 &&
      !!(this.plate as IFsLprResult as SnLprV3FlockRead).imageUrl
    );
  }

  public openExternalFlockV3Link(event: Event): void {
    event.stopPropagation();
    if (this.hasExternalFlockV3Link) {
      window.open((this.plate as IFsLprResult as SnLprV3FlockRead).imageUrl, '_blank');
    }
  }

  public nextImage(): void {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
    } else {
      // Loop back to first
      this.currentIndex = 0;
    }
  }

  public prevImage(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      // Looping back to last
      this.currentIndex = this.images.length - 1;
    }
  }

  public downloadImage(): void {
    if (this.plate) {
      this._plateImageDownloadService.downloadImage(this.plate, this.croppedImageUrl);
    }
  }
}
